import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChatMessage } from './ChatMessage';
import { TrendingUp, Send, Bot, Zap, BarChart3, PieChart, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { sendMCPEnhancedQuery, isPortfolioQuery } from '@/utils/mcpConnect';

interface EnhancedChatMessage extends ChatMessage {
  usedMCP?: boolean;
  type?: 'portfolio' | 'general';
}

const PortfolioChatComponent: React.FC = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState<EnhancedChatMessage[]>([
    {
      role: 'assistant',
      content: 'Hello! I\'m your AI investment assistant with access to your Zerodha portfolio data. I can provide personalized insights about your holdings, analyze performance, suggest diversification strategies, and answer general investment questions. What would you like to know?',
      timestamp: new Date(),
      type: 'general'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const isConnected = !!localStorage.getItem('kite_access_token');

  // Scroll to bottom of chat whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!input.trim() || !user?.id) return;
    
    // Add user message
    const userMessage: EnhancedChatMessage = {
      role: 'user',
      content: input,
      timestamp: new Date(),
      type: isPortfolioQuery(input) ? 'portfolio' : 'general'
    };
    
    setMessages(prevMessages => [...prevMessages, userMessage]);
    const currentInput = input;
    setInput('');
    setIsLoading(true);
    
    try {
      // Use MCP-enhanced query
      const { response, usedMCP } = await sendMCPEnhancedQuery(currentInput, user.id);
      
      // Add assistant message with response
      const assistantMessage: EnhancedChatMessage = {
        role: 'assistant',
        content: response,
        timestamp: new Date(),
        usedMCP: usedMCP,
        type: isPortfolioQuery(currentInput) ? 'portfolio' : 'general'
      };
      
      setMessages(prevMessages => [...prevMessages, assistantMessage]);
    } catch (error) {
      console.error('Error getting AI response:', error);
      toast.error('Failed to get a response. Please try again later.');
      
      // Add error message
      const errorMessage: EnhancedChatMessage = {
        role: 'assistant',
        content: "I'm sorry, I encountered an error while processing your request. Please try again later.",
        timestamp: new Date(),
        type: 'general'
      };
      
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Quick action buttons for common portfolio queries
  const quickActions = [
    {
      label: "Analyze my portfolio",
      query: "Analyze my current portfolio performance and provide insights",
      icon: BarChart3,
      disabled: !isConnected
    },
    {
      label: "Check diversification", 
      query: "How well diversified is my portfolio across sectors?",
      icon: PieChart,
      disabled: !isConnected
    },
    {
      label: "Risk assessment",
      query: "Assess the risk level of my current investments",
      icon: AlertTriangle,
      disabled: !isConnected
    },
    {
      label: "Performance review",
      query: "Which of my stocks are performing well and which need attention?",
      icon: TrendingUp,
      disabled: !isConnected
    }
  ];

  const handleQuickAction = (query: string) => {
    setInput(query);
  };

  return (
    <Card className="flex flex-col h-[700px] bg-gradient-to-br from-[#9E7C47]/20 to-[#9E7C47]/5 border-[#9E7C47]/30">
      <CardHeader className="border-b border-[#9E7C47]/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Bot className="h-5 w-5 mr-2 text-[#9E7C47]" />
            <div>
              <CardTitle className="text-lg">Portfolio AI Assistant</CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1">
                {isConnected ? (
                  <>
                    <Zap className="h-3 w-3 text-green-500" />
                    <span className="text-green-400">Connected to Zerodha</span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-3 w-3 text-amber-500" />
                    <span className="text-amber-400">Connect Zerodha for portfolio insights</span>
                  </>
                )}
              </CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      
      {/* Quick Actions */}
      <div className="p-4 border-b border-[#9E7C47]/30">
        <div className="flex flex-wrap gap-2">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <Button
                key={action.label}
                variant="outline"
                size="sm"
                onClick={() => handleQuickAction(action.query)}
                disabled={action.disabled || isLoading}
                className="text-xs bg-black/50 border-[#9E7C47]/30 hover:bg-[#9E7C47]/20"
              >
                <Icon className="h-3 w-3 mr-1" />
                {action.label}
              </Button>
            );
          })}
        </div>
      </div>
      
      <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div 
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div 
              className={`max-w-[85%] rounded-lg p-3 ${
                message.role === 'user' 
                  ? 'bg-[#9E7C47] text-white' 
                  : 'bg-black/50 border border-[#9E7C47]/30 text-white/90'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <p className="whitespace-pre-wrap flex-1">{message.content}</p>
                {message.role === 'assistant' && (
                  <div className="flex gap-1">
                    {message.usedMCP && (
                      <Badge variant="secondary" className="text-xs bg-green-500/20 text-green-400 border-green-500/30">
                        Portfolio Data
                      </Badge>
                    )}
                    {message.type === 'portfolio' && (
                      <Badge variant="secondary" className="text-xs bg-blue-500/20 text-blue-400 border-blue-500/30">
                        Portfolio Query
                      </Badge>
                    )}
                  </div>
                )}
              </div>
              <div className={`text-xs ${
                message.role === 'user' ? 'text-white/70' : 'text-white/50'
              }`}>
                {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-black/50 border border-[#9E7C47]/30 text-white/90 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <div className="h-2 w-2 bg-[#9E7C47] rounded-full animate-pulse"></div>
                <div className="h-2 w-2 bg-[#9E7C47] rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                <div className="h-2 w-2 bg-[#9E7C47] rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                <span className="text-sm">Analyzing your portfolio...</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </CardContent>
      
      <div className="p-4 border-t border-[#9E7C47]/30">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isConnected 
              ? "Ask about your portfolio, holdings, performance, or general investing..." 
              : "Ask about investing strategies, market trends, or connect Zerodha for portfolio insights..."
            }
            className="flex-1 bg-black/50 border-[#9E7C47]/30 text-white resize-none"
            rows={2}
            disabled={isLoading}
          />
          <Button
            onClick={handleSendMessage}
            disabled={isLoading || !input.trim()}
            className="bg-[#9E7C47] hover:bg-[#9E7C47]/90 text-white"
            type="submit"
          >
            {isLoading ? (
              <div className="h-4 w-4 border-t-2 border-wealth-dark rounded-full animate-spin"></div>
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default PortfolioChatComponent;